<template>
  <div class="res-wrapper" id="reservation" ref="el" :class="{ 'is-visible': isVisible }">
    <section class="res-section">

      <header class="res-header">
        <span class="res-eyebrow">{{ t('res_eyebrow') }}</span>
        <h2 class="res-titre">{{ t('res_title') }} <em>{{ t('res_title_em') }}</em></h2>
        <div class="res-divider">
          <div class="res-divider-line"></div>
          <div class="res-divider-dot"></div>
          <div class="res-divider-line"></div>
        </div>
      </header>

      <form class="res-form" @submit.prevent="envoyerReservation" novalidate>

        <div class="res-grid">
          <!-- Nom -->
          <div class="res-field">
            <label class="res-label">{{ t('res_label_nom') }} <span class="res-required">*</span></label>
            <input
              v-model="nom"
              class="res-input"
              type="text"
              :placeholder="t('res_placeholder_nom')"
              required
            />
          </div>

          <!-- Nombre de personnes -->
          <div class="res-field">
            <label class="res-label">{{ t('res_label_personnes') }} <span class="res-required">*</span></label>
            <input
              v-model="nbPersonnes"
              class="res-input"
              type="number"
              min="1"
              :placeholder="t('res_placeholder_personnes')"
              required
            />
          </div>

          <!-- Date -->
          <div class="res-field">
            <label class="res-label">{{ t('res_label_date') }} <span class="res-required">*</span></label>
            <input
              v-model="date"
              class="res-input"
              type="date"
              :min="today"
              :max="maxDate"
              @input="surChangementDate"
              required
            />
            <span class="res-field-hint">{{ t('res_hint_days') }}</span>
            <div v-if="jourFerme" class="res-ferme-warn">
              {{ t('res_ferme_warn') }}
            </div>
          </div>

          <!-- Heure -->
          <div class="res-field">
            <label class="res-label">{{ t('res_label_heure') }} <span class="res-required">*</span></label>
            <select v-model="heure" class="res-input res-select" required>
              <option disabled value="">{{ t('res_choose_heure') }}</option>
              <option v-for="h in heuresDisponibles" :key="h" :value="h">{{ h }}</option>
            </select>
          </div>
        </div>

        <!-- Note champs obligatoires -->
        <div class="res-note">
          <span v-html="t('res_note_required')"></span>
        </div>

        <!-- Message -->
        <div class="res-field res-field--full">
          <label class="res-label">{{ t('res_label_message') }}</label>
          <textarea
            v-model="messageLibre"
            class="res-input res-textarea"
            rows="4"
            :placeholder="t('res_placeholder_message')"
          ></textarea>
        </div>

        <!-- Bouton SMS -->
        <button type="button" class="res-btn-sms" @click="envoyer('sms')">
          📱 &nbsp;{{ t('res_btn_sms') }}
        </button>

      </form>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useReveal } from '@/composables/useReveal'
import { useLocale } from '@/i18n'
import { db } from '@/assets/firebase'
import { doc, setDoc, increment } from 'firebase/firestore'

const { el, isVisible } = useReveal()
const { t } = useLocale()

const nom = ref('')
const nbPersonnes = ref('')
const date = ref('')
const heure = ref('')
const messageLibre = ref('')

const numeroSMS = '0627392443'

const today = new Date().toISOString().split('T')[0]
const maxDate = new Date(new Date().setMonth(new Date().getMonth() + 3))
  .toISOString()
  .split('T')[0]

function formatDateFr(isoDate) {
  const d = new Date(isoDate)
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

const toutesLesHeures = [
  '12:00', '12:15', '12:30', '12:45',
  '13:00', '13:15',
  '19:00', '19:15', '19:30', '19:45',
  '20:00', '20:15', '20:30', '20:45',
  '21:00'
]

// Vendredi (5) et samedi (6) : pas de service du midi
const heuresDisponibles = computed(() => {
  if (!date.value) return toutesLesHeures
  const jour = new Date(date.value).getDay()
  if (jour === 5 || jour === 6) {
    return toutesLesHeures.filter(h => h >= '19:00')
  }
  return toutesLesHeures
})

const jourFerme = computed(() => {
  if (!date.value) return false
  const jour = new Date(date.value).getDay()
  return jour === 0 || jour === 1
})

function surChangementDate() {
  if (heure.value && !heuresDisponibles.value.includes(heure.value)) {
    heure.value = ''
  }
}

const messageReservation = computed(() => `Bonjour, je souhaite réserver une table :
Nom : ${nom.value}
Nombre de personnes : ${nbPersonnes.value}
Date : ${date.value ? formatDateFr(date.value) : ''}
Heure : ${heure.value}
Message : ${messageLibre.value || 'N/A'}
J'accepte que mon numéro soit utilisé afin d'être recontacté pour la confirmation de ma réservation.`)

const smsLink = computed(() => `sms:${numeroSMS}?&body=${encodeURIComponent(messageReservation.value)}`)

function envoyer(methode) {
  if (!nom.value || !nbPersonnes.value || !date.value || !heure.value) {
    alert(t('res_alert_fields_required'))
    return
  }
  if (jourFerme.value) {
    alert(t('res_alert_closed_day'))
    return
  }
  if (methode === 'sms') {
    const day = new Date().toISOString().split('T')[0]
    setDoc(doc(db, 'stats', 'reservations'), {
      total:             increment(1),
      [day.slice(0, 4)]: increment(1),
      [day.slice(0, 7)]: increment(1),
      [day]:             increment(1),
    }, { merge: true }).catch(() => {})
    window.open(smsLink.value, '_blank')
  }
}

function envoyerReservation() {
  envoyer('sms')
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital@0;1&display=swap');

/* ── Animation reveal ────────────────────────────── */
.res-wrapper {
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.75s ease, transform 0.75s ease;
}
.res-wrapper.is-visible {
  opacity: 1;
  transform: translateY(0);
}

/* ── Wrapper ─────────────────────────────────────── */
.res-wrapper {
  width: 100%;
  align-self: stretch;
  background: linear-gradient(160deg, #1a1912 0%, #13130e 55%, #111110 100%);
  border-top: 1px solid rgba(200, 160, 85, 0.2);
  position: relative;
  overflow: hidden;
}

.res-wrapper::before {
  content: '席';
  position: absolute;
  left: 50%;
  transform: translateX(-95%);
  top: 3rem;
  font-size: clamp(280px, 35vw, 480px);
  color: rgba(200, 160, 85, 0.04);
  font-family: 'Hiragino Mincho Pro', 'Yu Mincho', 'MS Mincho', serif;
  line-height: 1;
  pointer-events: none;
  user-select: none;
  z-index: 0;
}

.res-section {
  position: relative;
  z-index: 1;
}

.res-section {
  max-width: 720px;
  margin: 0 auto;
  padding: 5rem 1.5rem;
}

/* ── En-tête ─────────────────────────────────────── */
.res-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.res-eyebrow {
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: #8bbf30;
  margin-bottom: 1rem;
}

.res-titre {
  font-family: 'Playfair Display', serif;
  font-size: 2.4rem;
  font-weight: 400;
  color: #ede8df;
  margin: 0 0 1.5rem;
}

.res-titre em {
  font-style: italic;
  color: #c8a055;
}

/* ── Séparateur doré ─────────────────────────────── */
.res-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  max-width: 260px;
  margin: 0 auto;
}

.res-divider-line {
  flex: 1;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(200, 160, 85, 0.6));
}

.res-divider-line:last-child {
  background: linear-gradient(90deg, rgba(200, 160, 85, 0.6), transparent);
}

.res-divider-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #c8a055;
  flex-shrink: 0;
}

/* ── Formulaire ──────────────────────────────────── */
.res-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.res-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.res-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.res-field--full {
  grid-column: 1 / -1;
}

.res-label {
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #8bbf30;
}

.res-required {
  color: #c8a055;
}

.res-input {
  background: #1a1a14;
  border: 1px solid rgba(200, 160, 85, 0.22);
  border-radius: 4px;
  padding: 0.85rem 1rem;
  color: #ddd8cf;
  font-size: 0.9rem;
  font-family: inherit;
  transition: border-color 0.25s ease;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}

.res-input::placeholder {
  color: #4a4840;
}

.res-input:focus {
  border-color: rgba(200, 160, 85, 0.55);
}

.res-select {
  appearance: none;
  cursor: pointer;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23c8a055' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  padding-right: 2.5rem;
}

.res-select option {
  background: #1a1a14;
  color: #ddd8cf;
}

/* Date input calendar icon color */
.res-input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.5) sepia(1) saturate(2) hue-rotate(5deg);
  cursor: pointer;
  opacity: 0.6;
}

.res-textarea {
  resize: vertical;
  min-height: 110px;
}

/* ── Hint & avertissement jour fermé ─────────────── */
.res-field-hint {
  font-size: 0.65rem;
  color: #555048;
  letter-spacing: 0.1em;
  margin-top: 0.35rem;
}

.res-ferme-warn {
  margin-top: 0.5rem;
  padding: 0.6rem 0.9rem;
  border: 1px solid rgba(200, 100, 70, 0.35);
  border-radius: 4px;
  background: rgba(200, 80, 50, 0.06);
  font-size: 0.78rem;
  font-style: italic;
  color: #c0876a;
  line-height: 1.5;
}

/* ── Note champs obligatoires ────────────────────── */
.res-note {
  padding: 0.9rem 1.25rem;
  border: 1px solid rgba(139, 191, 48, 0.18);
  border-radius: 4px;
  background: rgba(139, 191, 48, 0.04);
  font-size: 0.8rem;
  font-style: italic;
  color: #8bbf30;
  text-align: center;
  line-height: 1.5;
}

/* ── Bouton SMS ──────────────────────────────────── */
.res-btn-sms {
  width: 100%;
  padding: 1.1rem;
  background: #8bbf30;
  border: none;
  border-radius: 4px;
  color: #ffffff;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.25s ease, transform 0.15s ease;
  margin-top: 0.5rem;
}

.res-btn-sms:hover {
  background: #9dd436;
  transform: translateY(-1px);
}

.res-btn-sms:active {
  transform: translateY(0);
}

/* ── Responsive ──────────────────────────────────── */
@media (max-width: 600px) {
  .res-grid {
    grid-template-columns: 1fr;
  }

  .res-titre {
    font-size: 1.8rem;
  }

  .res-section {
    padding: 3.5rem 1.25rem;
  }

  /* Empêche le zoom iOS sur focus */
  .res-input {
    font-size: 16px;
  }
}
</style>
